import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { CustomTruncateText } from "@ippon/shared-components";
import type { Unit, AreaType } from "@ippon/shared-types";

interface NavigationState {
  propName?: string;
  customerName?: string;
  privateAreaName?: string;
  fromSource?: string;
  [key: string]: unknown;
}

interface Props {
  icon: React.ReactNode;
  title: string;
  count: number;
  unit: string;
  items: Unit[];
  propertyId: string;
  propertyName: string;
  navigate: (path: string, options?: { state?: NavigationState }) => void;
  /** filter 用 area_type */
  areaType: AreaType;
}

/**
 * 物件一覧 → 物件カード内の area_type 別 Unit ミニリスト。
 *
 * UI は旧 PropertySectionList (grid + outlined Button + ページネーション) を踏襲。
 * 各 Unit カードをクリックすると `/properties/:propertyId/units/:unitId` の Unit 詳細へ遷移。
 */
export const PropertyUnitList: React.FC<Props> = ({
  icon,
  title,
  count,
  unit,
  items,
  propertyId,
  propertyName,
  navigate,
  areaType,
}) => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const [currentPage, setCurrentPage] = useState(1);

  const filtered = useMemo(
    () => items.filter((u) => u.area_type === areaType),
    [items, areaType],
  );

  const itemsPerPage = useMemo(() => {
    const columnsPerRow = isLgUp ? 4 : isMdUp ? 3 : 2;
    return columnsPerRow * 2;
  }, [isMdUp, isLgUp]);

  const pages = useMemo(() => {
    if (filtered.length === 0) return [];
    const result: Unit[][] = [];
    for (let i = 0; i < filtered.length; i += itemsPerPage) {
      result.push(filtered.slice(i, i + itemsPerPage));
    }
    return result;
  }, [filtered, itemsPerPage]);

  const totalPages = pages.length;

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const handleItemClick = (u: Unit) => {
    navigate(`/properties/${propertyId}/units/${u.unit_id}`, {
      state: {
        propName: propertyName,
        customerName: u.landmark_name,
        privateAreaName: u.landmark_name,
        fromSource: "composition",
      },
    });
  };

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
        <Box
          sx={{
            width: 16,
            height: 16,
            color: "#0B9DBD",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
        <Typography fontWeight={700} fontSize={14} color="#3e3e3e">
          {title}
        </Typography>
        <Typography fontSize={14} color="#6b7280">
          {count}
          {unit}
        </Typography>
      </Stack>

      <Box sx={{ mt: 0 }}>
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            width: "100%",
            clipPath: "inset(0)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              transform: `translateX(-${(currentPage - 1) * 100}%)`,
              transition: "transform 320ms cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            {pages.map((pageItems, pageIdx) => (
              <Box
                key={`page-${propertyId}-${pageIdx}`}
                sx={{
                  flex: "0 0 100%",
                  mr: "0",
                  boxSizing: "border-box",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, 1fr)",
                      md: "repeat(3, 1fr)",
                      lg: "repeat(4, 1fr)",
                    },
                    gap: 1.5,
                  }}
                >
                  {pageItems.map((u) => (
                    <Box key={u.unit_id} sx={{ minWidth: 0 }}>
                      <Button
                        variant="outlined"
                        color="inherit"
                        className="room-button"
                        onClick={(
                          e: React.MouseEvent<HTMLButtonElement>,
                        ) => {
                          e.stopPropagation();
                          handleItemClick(u);
                        }}
                        sx={{
                          width: "100%",
                          height: 34,
                          padding: "0 16px",
                          borderRadius: "10px",
                          borderColor: "#d9d9d9",
                          color: "#3e3e3e",
                          backgroundColor: "#ffffff",
                          fontFamily: '"Noto Sans JP"',
                          fontSize: "14px",
                          fontWeight: 400,
                          lineHeight: 1.7,
                          textTransform: "none",
                          "&:hover": {
                            backgroundColor: "#e9f6fa",
                          },
                        }}
                      >
                        <Box sx={{ width: "100%", minWidth: 0 }}>
                          <CustomTruncateText text={u.landmark_name} />
                        </Box>
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {totalPages > 1 && (
        <Stack
          direction="row"
          spacing={1}
          mt={1}
          justifyContent="center"
          alignItems="center"
        >
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              if (currentPage > 1) setCurrentPage(currentPage - 1);
            }}
            disabled={currentPage === 1}
            sx={{
              width: 32,
              height: 32,
              borderRadius: "6px",
              backgroundColor: "#f5f5f5",
              padding: "6px",
              "&:hover": { backgroundColor: "#e5e5e5" },
              "&:disabled": {
                backgroundColor: "#f5f5f5",
                opacity: 0.5,
              },
            }}
          >
            <ChevronLeftIcon
              sx={{ fontSize: 14, color: "rgba(0,0,0,0.88)" }}
            />
          </IconButton>
          {(() => {
            const maxDots = 5;
            const windowSize = Math.min(maxDots, totalPages);
            let start = Math.max(
              1,
              currentPage - Math.floor(windowSize / 2),
            );
            let end = start + windowSize - 1;
            if (end > totalPages) {
              end = totalPages;
              start = Math.max(1, end - windowSize + 1);
            }
            const pagesToShow = Array.from(
              { length: end - start + 1 },
              (_, i) => start + i,
            );
            return (
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  minWidth: 62,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {pagesToShow.map((pageNum) => (
                  <Box
                    key={`dot-${propertyId}-${pageNum}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentPage(pageNum);
                    }}
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor:
                        pageNum === currentPage ? "#6b7280" : "#e5e7eb",
                      cursor: "pointer",
                    }}
                  />
                ))}
              </Stack>
            );
          })()}
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              if (currentPage < totalPages) setCurrentPage(currentPage + 1);
            }}
            disabled={currentPage === totalPages}
            sx={{
              width: 32,
              height: 32,
              borderRadius: "6px",
              backgroundColor: "#f5f5f5",
              padding: "6px",
              "&:hover": { backgroundColor: "#e5e5e5" },
              "&:disabled": {
                backgroundColor: "#f5f5f5",
                opacity: 0.5,
              },
            }}
          >
            <ChevronRightIcon
              sx={{ fontSize: 14, color: "rgba(0,0,0,0.88)" }}
            />
          </IconButton>
        </Stack>
      )}
    </>
  );
};

export default PropertyUnitList;
